import {
  query,
  where,
  collectionGroup,
  doc,
  updateDoc,
  getDocs,
  writeBatch,
  setDoc,
} from "firebase/firestore";
import { format } from "date-fns";
import { db } from "../../firebase";
import {
  WORKFLOW_SCHEDULER_TYPES,
  WORKFLOW_WERKER_TYPES,
  FB_COLLECTION_PATH,
  PUSH_NOTIFICATION_TYPES,
} from "../../functions/postActionTypes";
import { fx_FB_SendNotification } from "../../functions/Fx_SendPushNotification";
import { fx_FB_WerkerFeedUpdateAll } from "../../functions/fx_FB_WerkerFeedUpdateAll";

interface JobActionsOptions {
  jobId: string;
  userId: string;
  userFirstName: string;
  jobPay?: string;
  jobCompany?: string;
  jobStartTime?: string;
  jobStartTimeRaw?: string;
  presentToast?: (options: any) => Promise<void>;
  onSuccess?: (action: string, message?: string) => void;
  onError?: (action: string, error: any) => void;
  onScheduledWerkersFound?: () => void;
  onConfirmReminder?: (callback: () => void) => void;
  onConfirmDelete?: (callback: () => void) => void;
}

/**
 * Shared hook for scheduler job actions
 * Extracted from mobile (SchedShiftDetail.tsx) - using mobile implementation as source of truth
 * 
 * Features:
 * - Send reminder to werkers
 * - Cancel job for all werkers
 * - Mark job complete/incomplete
 * - Mark job paid/unpaid
 * - Delete job with validation
 * 
 * @param options - Configuration object with job data and callbacks
 * @returns Object containing action functions
 */
export function useJobActions(options: JobActionsOptions) {
  const {
    jobId,
    userId,
    userFirstName,
    jobPay,
    jobCompany,
    jobStartTime,
    jobStartTimeRaw,
    presentToast,
    onSuccess,
    onError,
    onScheduledWerkersFound,
    onConfirmReminder,
    onConfirmDelete,
  } = options;

  /**
   * Send reminder notification to all invited werkers
   * Sets isRead: false on werker job docs and job feed
   */
  const sendReminder = async () => {
    try {
      const werkerUIDs: string[] = [];
      const unInviteRef = query(
        collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
        where("jobInfo.core.uniqueJobId", "==", jobId),
        where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Invited)
      );

      const querySnapshot = await getDocs(unInviteRef);

      // Use Promise.all for parallel updates instead of sequential loop
      await Promise.all(
        querySnapshot.docs.map(async (docSnap) => {
          const werkerJobDoc: any = docSnap.data();
          const docRef = docSnap.ref;

          const toUid = werkerJobDoc.docStamp.userInfoTo.uid;
          werkerUIDs.push(toUid);

          // Update both job doc and feed doc in parallel
          await Promise.all([
            updateDoc(docRef, {
              "jobInfo.status.isRead": false,
            }),
            setDoc(
              doc(db, FB_COLLECTION_PATH.Users, toUid, FB_COLLECTION_PATH.JobFeed, jobId),
              { isRead: false, updatedAt: Date.now() },
              { merge: true }
            ),
          ]);
        })
      );

      fx_FB_SendNotification(userId, werkerUIDs, PUSH_NOTIFICATION_TYPES.WerkerJobReminder, {
        schedFName: userFirstName,
        jobPay: jobPay || "",
        uniqueJobDocId: jobId,
        schedUid: userId,
      });

      onSuccess?.("reminder", "Reminder sent to werkers");

      // Show completion toast immediately after operation completes
      if (presentToast) {
        let message = "Re-alert complete";
        
        if (jobCompany && jobStartTimeRaw) {
          try {
            // Parse the raw timestamp and format as M/D/YY
            const dateObj = new Date(jobStartTimeRaw);
            const formattedDate = format(dateObj, "M/d/yy");
            message = `Completed re-alert for ${jobCompany} at ${formattedDate}`;
          } catch (error) {
            console.error("Error formatting date:", error);
            // Fallback to original format if parsing fails
            message = jobStartTime ? `Completed re-alert for ${jobCompany} at ${jobStartTime}` : `Re-alert complete for ${jobCompany}`;
          }
        }
        
        console.log("Showing completion toast:", message, "Company:", jobCompany, "Raw:", jobStartTimeRaw);
        
        await presentToast({
          message,
          duration: 3000,
          position: "top",
          color: "success",
        }).then(() => console.log("Completion toast presented successfully"))
          .catch(err => console.error("Toast error:", err));
      }
    } catch (error) {
      console.error("sendReminder error:", error);
      
      // Show error toast
      if (presentToast) {
        await presentToast({
          message: "Re-alert failed, please try again",
          duration: 2500,
          position: "top",
          color: "danger",
        });
      }
      
      onError?.("reminder", error);
    }
  };

  /**
   * Cancel job for scheduler and all werkers
   * Uses batch updates for werker docs
   *
   * @param waivePayment When true, sets `jobInfo.status.paymentWaivedByScheduler: true`
   *   on the scheduler's parent job doc and on every werker doc, so the cancelled
   *   jobs are excluded from the Payments → Unpaid list.
   */
  const cancelJob = async (waivePayment: boolean = false) => {
    const chunkArray = <T,>(arr: T[], size: number): T[][] => {
      const chunks: T[][] = [];
      for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
      return chunks;
    };

    try {
      const cancelJobRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobId);
      const parentUpdate: Record<string, unknown> = {
        "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Past,
        "jobInfo.status.jobCancelledByScheduler": true,
      };
      if (waivePayment) {
        parentUpdate["jobInfo.status.paymentWaivedByScheduler"] = true;
      }
      await updateDoc(cancelJobRef, parentUpdate);

      const q = query(collectionGroup(db, FB_COLLECTION_PATH.JobsDocs), where("jobInfo.core.uniqueJobId", "==", jobId));
      const snap = await getDocs(q);

      if (snap.empty) {
        await fx_FB_WerkerFeedUpdateAll(userId, userId, jobId, { action: "cancelled" });
        onSuccess?.("cancel", "Job cancelled");
        return;
      }

      const chunks = chunkArray(snap.docs, 450);
      for (const chunk of chunks) {
        const batch = writeBatch(db);

        chunk.forEach((d) => {
          const data: any = d.data();
          const toUid = data?.docStamp?.userInfoTo?.uid;
          if (!toUid || toUid === userId) return;

          const currentWerkerStatus = data?.jobInfo?.status?.jobStatusWerker;

          const nextWerkerStatus =
            currentWerkerStatus === WORKFLOW_WERKER_TYPES.Scheduled ||
            currentWerkerStatus === WORKFLOW_WERKER_TYPES.Past
              ? WORKFLOW_WERKER_TYPES.Past
              : WORKFLOW_WERKER_TYPES.Cancelled;

          const werkerUpdate: Record<string, unknown> = {
            "jobInfo.status.jobStatusWerker": nextWerkerStatus,
            "jobInfo.status.jobCancelledByScheduler": true,
          };
          if (waivePayment) {
            werkerUpdate["jobInfo.status.paymentWaivedByScheduler"] = true;
          }
          batch.update(d.ref, werkerUpdate);
        });

        await batch.commit();
      }

      await fx_FB_WerkerFeedUpdateAll(userId, userId, jobId, { action: "cancelled" });
      onSuccess?.("cancel", "Job cancelled");
    } catch (error) {
      console.error("cancelJob error:", error);
      onError?.("cancel", error);
    }
  };

  /**
   * Mark job as complete for scheduler and all scheduled werkers
   */
  const markComplete = async () => {
    try {
      const completeJobRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobId);
      await updateDoc(completeJobRef, {
        "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Past,
      });

      const completeWerkersRef = query(
        collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
        where("jobInfo.core.uniqueJobId", "==", jobId),
        where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Scheduled)
      );
      const querySnapshot = await getDocs(completeWerkersRef);
      
      const updatePromises = querySnapshot.docs.map((docSnap) =>
        updateDoc(docSnap.ref, {
          "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Past,
        })
      );
      
      await Promise.all(updatePromises);
      onSuccess?.("complete", "Job marked as complete");
    } catch (error) {
      console.error("markComplete error:", error);
      onError?.("complete", error);
    }
  };

  /**
   * Undo job completion (mark as Filled again)
   */
  const unCompleteJob = async () => {
    try {
      const schedFilledRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobId);
      await updateDoc(schedFilledRef, {
        "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
        "jobInfo.status.jobCancelledByScheduler": false,
      });

      const unCompleteWerkersRef = query(
        collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
        where("jobInfo.core.uniqueJobId", "==", jobId),
        where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Past)
      );
      const querySnapshot = await getDocs(unCompleteWerkersRef);
      
      const updatePromises = querySnapshot.docs.map((docSnap) =>
        updateDoc(docSnap.ref, {
          "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Scheduled,
          "jobInfo.status.jobCancelledByScheduler": false,
        })
      );
      
      await Promise.all(updatePromises);
      onSuccess?.("uncomplete", "Status changed back");
    } catch (error) {
      console.error("unCompleteJob error:", error);
      onError?.("uncomplete", error);
    }
  };

  /**
   * Mark job as paid for all werkers
   * Uses batch updates with retry logic
   */
  const markPaid = async () => {
    try {
      const paidRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobId);
      await updateDoc(paidRef, {
        "jobInfo.status.isPaidJob": true,
      });
      onSuccess?.("paid", "Job marked as paid");
    } catch (error) {
      console.error("markPaid error:", error);
      onError?.("paid", error);
    }
  };

  /**
   * Undo job paid status
   */
  const unCloseOut = async () => {
    try {
      const paidRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobId);
      await updateDoc(paidRef, {
        "jobInfo.status.isPaidJob": false,
        "jobInfo.status.isPaidWerkers": false,
      });

      onSuccess?.("unpaid", "Job marked as unpaid");
    } catch (error) {
      console.error("unCloseOut error:", error);
      onError?.("unpaid", error);
    }
  };

  /**
   * Undo only the job paid flag (does not change isPaidWerkers)
   */
  const unMarkPaid = async () => {
    try {
      const paidRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobId);
      await updateDoc(paidRef, {
        "jobInfo.status.isPaidJob": false,
      });
      onSuccess?.("unpaid", "Job marked as unpaid");
    } catch (error) {
      console.error("unMarkPaid error:", error);
      onError?.("unpaid", error);
    }
  };

  /**
   * Delete job with validation
   * Checks if anyone is still scheduled first
   */
  const deleteJob = async () => {
    try {
      const q = query(collectionGroup(db, FB_COLLECTION_PATH.JobsDocs), where("jobInfo.core.uniqueJobId", "==", jobId));
      const snap = await getDocs(q);
      
      if (snap.empty) {
        onSuccess?.("delete", "Job deleted");
        return;
      }

      const anyoneScheduled = snap.docs.some((d) => {
        const data: any = d.data();
        return data?.jobInfo?.status?.jobStatusWerker === WORKFLOW_WERKER_TYPES.Scheduled ||
               data?.jobInfo?.status?.jobStatusWerker === WORKFLOW_WERKER_TYPES.Past;
      });

      if (anyoneScheduled) {
        onScheduledWerkersFound?.();
        return;
      }

      // Callback to allow parent to show confirmation dialog
      const executeDelete = async () => {
        try {
          const batch = writeBatch(db);

          snap.docs.forEach((d) => {
            const data: any = d.data();
            const toUid = data?.docStamp?.userInfoTo?.uid;

            batch.update(d.ref, {
              "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Deleted,
              "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Deleted,
            });

            if (toUid) {
              const feedRef = doc(db, FB_COLLECTION_PATH.Users, toUid, FB_COLLECTION_PATH.JobFeed, jobId);
              batch.delete(feedRef);
            }
          });

          await batch.commit();
          onSuccess?.("delete", "Job deleted");
        } catch (error) {
          console.error("Delete commit error:", error);
          onError?.("delete", error);
        }
      };

      // Call confirmation callback if provided, otherwise execute directly
      if (onConfirmDelete) {
        onConfirmDelete(executeDelete);
      } else {
        await executeDelete();
      }
    } catch (error) {
      console.error("deleteJob error:", error);
      onError?.("delete", error);
    }
  };

  /**
   * Wrapper for sendReminder that shows confirmation first
   */
  const handleReminder = () => {
    if (onConfirmReminder) {
      onConfirmReminder(sendReminder);
    } else {
      sendReminder();
    }
  };

  return {
    sendReminder,
    handleReminder,
    cancelJob,
    markComplete,
    unCompleteJob,
    markPaid,
    unCloseOut,
    unMarkPaid,
    deleteJob,
  };
}
